import Button from '@jetbrains/ring-ui/components/button/button'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {memo, useCallback, useContext} from 'react'

import {restApi} from '../../../../../services/rest'
import {EXPAND_ARROW_WIDTH, TREE_INDENT_WIDTH} from '../../../../common/Tree/Tree.constants'
import type {Node} from '../../../../common/Tree/Tree.types'
import {TreeEntityType} from '../../BuildProblems.types'
import {BuildProblemsTreeContext} from '../BuildProblemsTree.context'

import styles from './BuildProblemsTreeNodeFooter.css'

type Props = {
  level?: number
  node: Node
  type: TreeEntityType
  childrenCount: number | null | undefined
  count: number | null | undefined
  treeLocator: string
  depth: number
}

function BuildProblemsTreeNodeFooter({
  level = 0,
  type,
  count,
  childrenCount,
  node,
  treeLocator,
  depth,
}: Props) {
  const subTreeRootId = node?.value

  const {locatorInEndpoint, subTreeRootIdInLocator, getEndpoint} =
    useContext(BuildProblemsTreeContext)

  const [fetchProblemOccurrenceSubtree, {loading}] =
    restApi.endpoints.getProblemOccurrenceTree.useLazyQuery({
      selectFromResult: ({isLoading}) => ({loading: isLoading}),
    })

  const onClick = useCallback(() => {
    fetchProblemOccurrenceSubtree({
      endpoint: getEndpoint(treeLocator),
      subTreeRootId: subTreeRootIdInLocator ? null : subTreeRootId,
      locator: locatorInEndpoint
        ? null
        : [treeLocator, subTreeRootIdInLocator ? `subTreeRootId:${subTreeRootId}` : null]
            .filter(Boolean)
            .join(','),
      subTreeKey: subTreeRootId,
      treeLocator,
      depth,
    })
  }, [
    depth,
    fetchProblemOccurrenceSubtree,
    getEndpoint,
    locatorInEndpoint,
    subTreeRootId,
    subTreeRootIdInLocator,
    treeLocator,
  ])

  const overallChildrenCount = type === TreeEntityType.PROBLEM_TYPE ? count : childrenCount
  const style = {paddingLeft: level * TREE_INDENT_WIDTH + EXPAND_ARROW_WIDTH}

  return overallChildrenCount != null &&
    node.children != null &&
    node.children.length < overallChildrenCount ? (
    <div style={style}>
      <Button className={styles.button} onClick={onClick} disabled={loading} text>
        {`Show all ${overallChildrenCount} items`}
      </Button>
      {loading && <LoaderInline />}
    </div>
  ) : null
}

export default memo<Props>(BuildProblemsTreeNodeFooter)
